/* ───────────────────────────────────────────────────────────────────────────
   O16 PDP Reassurance — live cart sync (round 5, bundle-aware round 6)

   PROBLEM: snippets/o16-cart-reassurance.liquid renders the "Shipping {value}"
   line + free-ship note + "WANT FREE SHIPPING?" nudge in BOTH the cart drawer
   AND the PDP (via snippets/main-product-section.liquid). The cart drawer
   updates live after every AJAX cart mutation because cartDrawer.renderContents()
   fully re-renders the cart-drawer section. The PDP copy has no such re-render
   path — it is server-rendered once at page load and used to go stale (still
   read "59 kr" + still show the nudge) until a full reload.

   FIX: subscribe to PUB_SUB_EVENTS.cartUpdate (published by product-form.js,
   assets/cart.js and the o16-cookbook-add nudge after EVERY cart add, remove
   or quantity change — see assets/cart.js ~L300 and
   assets/o16-cart-drawer.js ~L97) and flip the already-rendered PDP markup:
     - shipping value  → swap between the two PRE-COMPUTED strings baked into
       data-ship-value-paid / data-ship-value-free on the value span (both
       rendered server-side in o16-cart-reassurance.liquid) — this file never
       re-implements the geo shipping-rate lookup (DK / EU / zone2 / zone3 / ROW).
     - free-ship note  → toggle the `hidden` attribute (copy is already correct)
     - free-ship nudge → toggle the `hidden` attribute (markup is already correct)
   No section fetch, no markup rebuild — o16-cart-reassurance.liquid renders the
   note + nudge unconditionally now (hidden via the `hidden` attribute rather
   than omitted) precisely so this script only ever needs to flip visibility.

   BUNDLE FIX (round 6): the free-ship product can qualify a cart either
   directly OR as a component inside a Shopify native bundle line item
   (line_item.item_components — confirmed live: the-bestsellers → components
   include OSTERIA 16 NO. 1). item_components is CART-ONLY Liquid and is NOT
   present on /cart.js, so this script can no longer determine "in cart" by
   fetching /cart.js and matching data-fs-product-id against item.product_id —
   that misses every bundle. Instead it reads the status Liquid ALREADY computed
   (snippets/o16-freeship-in-cart.liquid, item_components-aware) off the cart
   drawer's own DOM: PUB_SUB_EVENTS.cartUpdate fires AFTER
   cartDrawer.renderContents() has replaced #CartDrawer's contents with a fresh
   Section-Rendering-API render (see assets/cart-drawer.js — comment at L1083),
   so by the time this handler runs, `cart-drawer [data-fs-in-cart]` is
   guaranteed current — no extra network round-trip needed at all.
   Falls back to the /cart.js direct-match method (documented, non-bundle-aware)
   only if no cart drawer is present on the page (settings.cart_type == 'page';
   the store currently runs 'drawer', the schema default).

   Runs on every product page (loaded from sections/main-product.liquid).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  function getWrappers() {
    return document.querySelectorAll('[data-o16-pdp-reassurance]');
  }

  function applyState(wrapper, inCart) {
    // Some products grant free shipping just by being bought — the free-ship
    // product itself, and the bundles the owner lists under
    // settings.o16_freeship_bundles. On their own page the promise holds
    // whatever the cart currently contains, so the cart must not be allowed to
    // take it back. Baked server-side in snippets/main-product-section.liquid.
    if (wrapper.dataset.fsSelfGrants === 'true') inCart = true;

    var valueEl = wrapper.querySelector('[data-o16-ship-value]');
    if (valueEl) {
      var paid = valueEl.getAttribute('data-ship-value-paid');
      var free = valueEl.getAttribute('data-ship-value-free');
      var next = inCart ? free : paid;
      if (next != null) {
        valueEl.textContent = next;
      }
    }

    var noteEl = wrapper.querySelector('[data-o16-ship-note]');
    if (noteEl) {
      noteEl.hidden = !inCart;
    }

    var nudgeEl = wrapper.querySelector('.o16-freeship-nudge');
    if (nudgeEl) {
      nudgeEl.hidden = inCart;
    }
  }

  function applyToAllWrappers(inCart) {
    getWrappers().forEach(function (wrapper) {
      applyState(wrapper, inCart);
    });
  }

  /* Preferred path — read the already-fresh, item_components-aware status the
     cart drawer just re-rendered server-side. Returns true/false, or null if
     no cart drawer is present on this page (e.g. settings.cart_type == 'page'). */
  function readInCartFromDrawer() {
    var drawerStatusEl = document.querySelector('cart-drawer [data-fs-in-cart]');
    if (!drawerStatusEl) return null;
    return drawerStatusEl.getAttribute('data-fs-in-cart') === 'true';
  }

  /* Fallback path — direct-product match only (bundles are NOT detected here,
     since /cart.js never exposes item_components). Only used when no cart
     drawer exists on the page to read a bundle-aware status from. */
  function syncFromCartJs() {
    var wrappers = getWrappers();
    if (!wrappers.length) return;

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        wrappers.forEach(function (wrapper) {
          var productId = wrapper.getAttribute('data-fs-product-id');
          if (!productId) return;
          var inCart = items.some(function (item) {
            return String(item.product_id) === String(productId);
          });
          applyState(wrapper, inCart);
        });
      })
      .catch(function () {
        /* Network hiccup — leave the PDP copy as last rendered rather than guess. */
      });
  }

  function sync() {
    if (!getWrappers().length) return;

    var inCart = readInCartFromDrawer();
    if (inCart !== null) {
      applyToAllWrappers(inCart);
      return;
    }

    syncFromCartJs();
  }

  function init() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Small delay to let the DOM settle after renderContents — base theme's
      // product-form.js actually publishes cartUpdate BEFORE calling
      // this.cart.renderContents(response) (cart-drawer re-render happens
      // AFTER the publish, in the same synchronous call chain), so reading
      // cart-drawer's [data-fs-in-cart] synchronously on the event can catch
      // pre-render (stale) markup. Same defensive pattern already used by
      // o16FillDeliveryDate in assets/o16-cart-drawer.js (~L1088).
      setTimeout(sync, 100);
    });
  }

  init();
})();
